import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
// Store app (Task C4 staff sign-in): relative URLs would hit capacitor://localhost. apiUrl is the
// identity on the web, and credentialsMode() is 'include' there — web behaviour is unchanged.
import { apiUrl, credentialsMode } from "@/lib/net";
import { Shield, Smartphone, Key, Loader2, MessageSquare } from "lucide-react";

const verificationSchema = z.object({
  otp: z.string().length(6, "Please enter the 6-digit code"),
});

const backupSchema = z.object({
  backupCode: z.string().min(8, "Please enter a valid backup code")
});

interface MFAVerificationProps {
  userId: number;
  loginAttemptId: string;
  onSuccess: (token: string, user: any) => void;
  onError: (message: string) => void;
}

export function MFAVerification({ userId, loginAttemptId, onSuccess, onError }: MFAVerificationProps) {
  const [method, setMethod] = useState<'otp' | 'backup'>('otp');
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(true); // Code is automatically sent during login
  const { toast } = useToast();

  const otpForm = useForm<z.infer<typeof verificationSchema>>({
    resolver: zodResolver(verificationSchema),
    defaultValues: {
      otp: "",
    }
  });

  const backupForm = useForm<z.infer<typeof backupSchema>>({
    resolver: zodResolver(backupSchema),
    defaultValues: {
      backupCode: ""
    }
  });

  const sendMFACode = async () => {
    setIsLoading(true);
    try {
      const response = await apiRequest('/api/auth/mfa/send-code', {
        method: 'POST',
        data: { loginAttemptId }
      });

      if (response.ok) {
        setCodeSent(true);
        toast({
          title: "Code Sent",
          description: "Verification code sent to your WhatsApp number",
        });
      } else {
        const error = await response.json();
        onError(error.message || "Failed to send verification code");
      }
    } catch (error: any) {
      onError(error.message || "Failed to send verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOTP = async (data: z.infer<typeof verificationSchema>) => {
    setIsLoading(true);
    try {
      // Use direct fetch to avoid apiRequest complications
      const response = await fetch(apiUrl('/api/auth/mfa/verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: data.otp,
          loginAttemptId,
        }),
        credentials: credentialsMode()
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Verification Successful",
          description: "Access granted to your account",
        });
        onSuccess(result.token, result.user);
      } else {
        const errorText = await response.text();
        try {
          const error = JSON.parse(errorText);
          onError(error.message || "Invalid verification code");
        } catch {
          onError(`HTTP ${response.status}: ${errorText}`);
        }
      }
    } catch (error: any) {
      onError(error.message || "Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const verifyBackupCode = async (data: z.infer<typeof backupSchema>) => {
    setIsLoading(true);
    try {
      // Audit 2026-09-27 M7: the login-time backup endpoint, bound to the same
      // server-issued attempt as the WhatsApp code. (/api/auth/mfa/verify-backup needs
      // a session, which does not exist yet at this step, so it always 401'd.)
      const response = await fetch(apiUrl('/api/auth/mfa/verify-backup-login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ backupCode: data.backupCode.trim(), loginAttemptId }),
        credentials: credentialsMode()
      });

      const result = await response.json().catch(() => ({}));
      if (response.ok && result.token && result.user) {
        toast({
          title: "Backup Code Verified",
          description: `Access granted. ${result.remainingCodes} backup codes remaining.`,
        });
        onSuccess(result.token, result.user);
      } else {
        onError(result.message || "Invalid backup code");
      }
    } catch (error: any) {
      onError(error.message || "Backup code verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const generateDeviceFingerprint = (): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx?.fillText('fingerprint', 2, 2);
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');
    
    return btoa(fingerprint).substring(0, 32);
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    otpForm.setValue('otp', value);
  };

  if (method === 'backup') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
            <Key className="w-6 h-6 text-orange-600" />
          </div>
          <CardTitle>Enter Backup Code</CardTitle>
          <CardDescription>
            Use one of your saved backup codes to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...backupForm}>
            <form onSubmit={backupForm.handleSubmit(verifyBackupCode)} className="space-y-4">
              <FormField
                control={backupForm.control}
                name="backupCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Backup Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter 8-character backup code"
                        className="text-center font-mono"
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading || !backupForm.watch('backupCode')}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Backup Code'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setMethod('otp')}
                  disabled={isLoading}
                >
                  Use WhatsApp Code Instead
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
          <Shield className="w-6 h-6 text-blue-600" />
        </div>
        <CardTitle>Verify Your Identity</CardTitle>
        <CardDescription>
          Enter the verification code sent to your WhatsApp
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!codeSent ? (
          <div className="space-y-4">
            <Alert>
              <MessageSquare className="h-4 w-4" />
              <AlertDescription>
                We'll send a 6-digit verification code to your registered WhatsApp number.
              </AlertDescription>
            </Alert>

            <Button 
              onClick={sendMFACode}
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending Code...
                </>
              ) : (
                <>
                  <Smartphone className="mr-2 h-4 w-4" />
                  Send Verification Code
                </>
              )}
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => setMethod('backup')}
              disabled={isLoading}
            >
              Use Backup Code Instead
            </Button>
          </div>
        ) : (
          <Form {...otpForm}>
            <form onSubmit={otpForm.handleSubmit(verifyOTP)} className="space-y-4">
              <FormField
                control={otpForm.control}
                name="otp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Verification Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={handleOtpChange}
                        placeholder="123456"
                        className="text-center text-lg tracking-widest"
                        maxLength={6}
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading || otpForm.watch('otp').length !== 6}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Continue'
                  )}
                </Button>

                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={sendMFACode}
                    disabled={isLoading}
                  >
                    Resend Code
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setMethod('backup')}
                    disabled={isLoading}
                  >
                    Use Backup Code
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
}